import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  GraduationCap, Award, CheckCircle, Info, ChevronRight, 
  Users, BookOpen, Target, Star, ArrowRight, FileText, Download
} from 'lucide-react';
import Layout from '../layout/Layout';

// Données des niveaux CECRL selon Cambridge
const cefrLevels = [
  {
    level: 'Pre A1',
    name: 'Starters',
    category: 'Young Learners',
    color: 'bg-emerald-500',
    lightColor: 'bg-emerald-50',
    textColor: 'text-emerald-700',
    description: 'Premier contact avec l\'anglais. Compréhension et expression très basiques.',
    skills: ['Vocabulaire de base', 'Compréhension simple', 'Expression orale basique'],
    cambridgeScale: '80-99'
  },
  {
    level: 'A1',
    name: 'Movers',
    category: 'Young Learners',
    color: 'bg-green-500',
    lightColor: 'bg-green-50',
    textColor: 'text-green-700',
    description: 'Utilisateur élémentaire. Peut comprendre et utiliser des expressions familières.',
    skills: ['Présentations simples', 'Questions basiques', 'Interactions de base'],
    cambridgeScale: '100-119'
  },
  {
    level: 'A2',
    name: 'Flyers / Key',
    category: 'Young Learners / Schools',
    color: 'bg-teal-500',
    lightColor: 'bg-teal-50',
    textColor: 'text-teal-700',
    description: 'Peut communiquer lors de tâches simples. Décrit son environnement immédiat.',
    skills: ['Conversations simples', 'Lecture de textes courts', 'Rédaction basique'],
    cambridgeScale: '120-139',
    exams: ['A2 Flyers', 'A2 Key for Schools']
  },
  {
    level: 'B1',
    name: 'Preliminary',
    category: 'For Schools / General',
    color: 'bg-blue-500',
    lightColor: 'bg-blue-50',
    textColor: 'text-blue-700',
    description: 'Utilisateur indépendant. Peut se débrouiller dans la plupart des situations.',
    skills: ['Conversations courantes', 'Textes sur sujets familiers', 'Argumentation simple'],
    cambridgeScale: '140-159',
    exams: ['B1 Preliminary for Schools', 'B1 Preliminary']
  },
  {
    level: 'B2',
    name: 'First',
    category: 'For Schools / General',
    color: 'bg-indigo-500',
    lightColor: 'bg-indigo-50',
    textColor: 'text-indigo-700',
    description: 'Peut comprendre des textes complexes. S\'exprime de façon claire et détaillée.',
    skills: ['Discussions spontanées', 'Textes complexes', 'Arguments développés'],
    cambridgeScale: '160-179',
    exams: ['B2 First for Schools', 'B2 First'],
    highlight: true
  },
  {
    level: 'C1',
    name: 'Advanced',
    category: 'General / Professional',
    color: 'bg-purple-500',
    lightColor: 'bg-purple-50',
    textColor: 'text-purple-700',
    description: 'Utilisateur expérimenté. Peut s\'exprimer de façon fluide et spontanée.',
    skills: ['Communication fluide', 'Textes académiques', 'Usage flexible de la langue'],
    cambridgeScale: '180-199',
    exams: ['C1 Advanced']
  },
  {
    level: 'C2',
    name: 'Proficiency',
    category: 'Proficiency',
    color: 'bg-rose-500',
    lightColor: 'bg-rose-50',
    textColor: 'text-rose-700',
    description: 'Maîtrise parfaite. Peut comprendre sans effort et s\'exprimer de façon précise.',
    skills: ['Maîtrise complète', 'Nuances subtiles', 'Expression native'],
    cambridgeScale: '200-230',
    exams: ['C2 Proficiency']
  }
];

const CambridgePage = () => {
  return (
    <Layout>
      <div className="min-h-screen bg-white" data-testid="cambridge-page">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-[#285BBB] to-[#2C3C65] py-20 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'}} />
          </div>
          <div className="max-w-7xl mx-auto px-4 text-center relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full text-white text-sm font-medium mb-4">
                <Award className="h-4 w-4" />
                Centre agréé Cambridge
              </span>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Certifications Cambridge English
              </h1>
              <p className="text-xl text-blue-100 max-w-3xl mx-auto">
                Des certifications reconnues mondialement pour valider votre niveau d'anglais
              </p>
            </motion.div>
          </div>
        </section>

        {/* Principe important */}
        <section className="py-8 bg-amber-50 border-b border-amber-200">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-start gap-4">
              <Info className="h-8 w-8 text-amber-600 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-amber-800 text-lg">Principe essentiel</h3>
                <p className="text-amber-700">
                  <strong>Le niveau de certification n'est pas défini par l'âge, mais par votre niveau d'anglais réel.</strong> 
                  Un adulte débutant peut commencer au niveau A1, tandis qu'un adolescent avancé peut passer le C1. 
                  C'est pourquoi nous proposons un <strong>cours d'évaluation gratuit</strong> pour définir votre niveau exact.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Logos Cambridge */}
        <section className="py-12 bg-[#F5F7FF]">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-3xl font-bold text-[#2C3C65] mb-4">Centre agréé Cambridge</h2>
                <p className="text-[#6777A1] mb-6">
                  The English Speaking Center est un centre de préparation et d'examen agréé Cambridge English.
                  Nous préparons nos élèves à tous les niveaux de certification, du Pre A1 au C2.
                </p>
                <div className="bg-white p-4 rounded-lg shadow-sm inline-block">
                  <img 
                    src="/cambridge-assessment.png" 
                    alt="Cambridge Assessment" 
                    className="h-14 w-auto"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { number: '25 000+', label: 'Organisations reconnaissent Cambridge' },
                  { number: 'À vie', label: 'Validité des certifications' },
                  { number: '100%', label: 'Universités UK acceptent C1/C2' },
                ].map((stat, idx) => (
                  <div key={idx} className="bg-white p-4 rounded-xl shadow-sm text-center">
                    <div className="text-2xl font-bold text-[#285BBB]">{stat.number}</div>
                    <div className="text-xs text-[#6777A1]">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Diagramme CEFR */}
        <section className="py-8 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <h3 className="text-2xl font-bold text-[#2C3C65] mb-6 text-center">Échelle des niveaux Cambridge English</h3>
            <img 
              src="/cambridge-cefr.jpg" 
              alt="Cambridge English Qualifications - CEFR Levels" 
              className="w-full max-w-5xl mx-auto rounded-xl shadow-lg"
            />
          </div>
        </section>

        {/* Tableau des niveaux CECRL */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-[#2C3C65] mb-4">
                Les niveaux du Cadre Européen (CECRL)
              </h2>
              <p className="text-[#6777A1] max-w-2xl mx-auto">
                Chaque certification Cambridge correspond à un niveau précis du CECRL. 
                Trouvez le niveau qui correspond à vos compétences actuelles.
              </p>
            </div>

            {/* Grille des niveaux */}
            <div className="space-y-4">
              {cefrLevels.map((level, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  viewport={{ once: true }}
                  className={`relative overflow-hidden rounded-xl border-2 ${
                    level.highlight 
                      ? 'border-[#285BBB] shadow-lg' 
                      : 'border-gray-100'
                  }`}
                >
                  {level.highlight && (
                    <div className="absolute top-0 right-0 bg-[#285BBB] text-white text-xs px-3 py-1 rounded-bl-lg font-medium">
                      Niveau Bac recommandé
                    </div>
                  )}
                  <div className="flex flex-col md:flex-row">
                    {/* Badge niveau */}
                    <div className={`${level.color} p-6 md:w-32 flex flex-col items-center justify-center text-white`}>
                      <span className="text-3xl font-bold">{level.level}</span>
                      <span className="text-xs opacity-80">{level.cambridgeScale}</span>
                    </div>
                    
                    {/* Contenu */}
                    <div className="flex-1 p-6">
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-xl font-bold text-[#2C3C65]">
                              Cambridge {level.name}
                            </h3>
                            <span className={`text-xs px-2 py-1 ${level.lightColor} ${level.textColor} rounded-full font-medium`}>
                              {level.category}
                            </span>
                          </div>
                          <p className="text-[#6777A1] mb-3">{level.description}</p>
                          <div className="flex flex-wrap gap-2">
                            {level.skills.map((skill, sidx) => (
                              <span key={sidx} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                        
                        {/* Examens disponibles */}
                        {level.exams && (
                          <div className="md:w-48 md:text-right">
                            <p className="text-xs text-gray-500 mb-2">Examens disponibles :</p>
                            <div className="flex flex-wrap md:justify-end gap-1">
                              {level.exams.map((exam, eidx) => (
                                <span key={eidx} className={`text-xs px-2 py-1 ${level.lightColor} ${level.textColor} rounded font-medium`}>
                                  {exam}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Tests Multilevels */}
        <section className="py-16 bg-[#F5F7FF]">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <span className="text-sm font-semibold text-[#285BBB] uppercase tracking-wider">Tests adaptatifs</span>
                <h2 className="text-3xl font-bold text-[#2C3C65] mt-2 mb-4">Linguaskill & IELTS</h2>
                <p className="text-[#6777A1] mb-6">
                  En plus des certifications traditionnelles, nous préparons également aux tests adaptatifs 
                  qui évaluent tous les niveaux en une seule session.
                </p>
                <div className="space-y-4">
                  <div className="bg-white p-4 rounded-xl shadow-sm">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                        <Target className="h-5 w-5 text-yellow-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-[#2C3C65]">Linguaskill</h4>
                        <p className="text-xs text-[#6777A1]">Test adaptatif en ligne - Résultats en 48h</p>
                      </div>
                    </div>
                    <p className="text-sm text-[#6777A1]">
                      Idéal pour le CPF. Évalue automatiquement votre niveau de A1 à C1+.
                    </p>
                  </div>
                  <div className="bg-white p-4 rounded-xl shadow-sm">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                        <Star className="h-5 w-5 text-red-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-[#2C3C65]">IELTS</h4>
                        <p className="text-xs text-[#6777A1]">Test international reconnu</p>
                      </div>
                    </div>
                    <p className="text-sm text-[#6777A1]">
                      Requis pour les études et l'immigration dans les pays anglophones.
                    </p>
                  </div>
                </div>
              </div>
              <div>
                <img 
                  src="https://customer-assets.emergentagent.com/job_c50557b7-c6af-4f23-b853-f16a4e32702b/artifacts/p009kree_CEFR_Certificate_diagram_0625_RGB_960px.jpg"
                  alt="Cambridge English Scale"
                  className="w-full rounded-xl shadow-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Flyer Parents */}
        <section className="py-16 bg-white" data-testid="parents-flyer-section">
          <div className="max-w-5xl mx-auto px-4">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-3 rounded-full bg-amber-100 border border-amber-200">
                <Users className="h-4 w-4 text-amber-700" />
                <span className="text-amber-800 text-xs uppercase tracking-widest font-semibold">Pour les parents</span>
              </div>
              <h2 className="text-3xl font-bold text-[#2C3C65] mb-3">
                Guide officiel Cambridge pour les parents
              </h2>
              <p className="text-[#6777A1] max-w-2xl mx-auto">
                Tout ce que vous devez savoir sur les examens Cambridge Young Learners : Starters, Movers, Flyers.
                Préparez votre enfant en toute sérénité grâce à ce guide officiel Cambridge.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 items-center">
              <a
                href="https://customer-assets.emergentagent.com/job_e21507e1-a0e4-40f2-a327-b19a660901a9/artifacts/7mrzdccj_476772-parents-flyer.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="block mx-auto max-w-[220px] rounded-2xl overflow-hidden shadow-xl border border-slate-200 bg-white group transition-transform hover:scale-[1.02]"
                data-testid="parents-flyer-cover-link"
                aria-label="Ouvrir le flyer Cambridge dans un nouvel onglet"
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_e21507e1-a0e4-40f2-a327-b19a660901a9/artifacts/44nlciyt_couverture_flyer_cambridge.PNG"
                  alt="Couverture du flyer Cambridge English Qualifications pour parents"
                  className="w-full h-auto object-cover"
                  data-testid="parents-flyer-cover-image"
                  loading="lazy"
                />
              </a>

              <div className="space-y-4">
                <h3 className="text-2xl font-bold text-[#2C3C65]">
                  Aidez votre enfant à réussir
                </h3>
                <ul className="space-y-3">
                  {[
                    'Comprendre les niveaux Starters, Movers et Flyers',
                    'Suivre les progrès et la motivation à la maison',
                    'Conseils pour bien préparer le jour de l\'examen',
                    'Les avantages des certifications Cambridge à vie',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span className="text-[#2C3C65]">{item}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href="https://customer-assets.emergentagent.com/job_e21507e1-a0e4-40f2-a327-b19a660901a9/artifacts/7mrzdccj_476772-parents-flyer.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  download="cambridge-parents-flyer.pdf"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#285BBB] hover:bg-[#1e47a0] text-white font-semibold rounded-xl transition-colors shadow-md"
                  data-testid="download-parents-flyer"
                >
                  <Download className="h-4 w-4" />
                  Télécharger le flyer (PDF)
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* CTA - Évaluation gratuite */}
        <section className="py-16 bg-gradient-to-r from-[#285BBB] to-[#2C3C65]">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <CheckCircle className="h-12 w-12 text-white/80 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-white mb-4">
              Définissez votre niveau avec un cours d'évaluation gratuit
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Nous vous proposons un cours d'évaluation gratuit pour déterminer votre niveau exact 
              et vous orienter vers la certification adaptée.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link
                to="/demande-info"
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#ff3a2d] text-white font-bold rounded-lg hover:bg-[#e6352a] transition-colors shadow-lg"
              >
                Demander une évaluation gratuite
                <ArrowRight className="h-5 w-5" />
              </Link>
              <Link
                to="/programmes/adultes"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white/10 text-white font-bold rounded-lg hover:bg-white/20 transition-colors border border-white/30"
              >
                Voir nos formations CPF
              </Link>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default CambridgePage;
